import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { map, catchError, switchMap, withLatestFrom, concatMap, filter, take, exhaustMap, tap } from 'rxjs/operators';
import { AvatarActions } from './avatar.actions';
import { AvatarApiService } from '@app/core/api/avatar';
import { LoggerService } from '@app/core/infrastructure';
import { UserPreferencesFacade } from '@app/store/user-preferences/user-preferences.facade';
import { ToastNotificationService } from '@app/core/infrastructure';
import { selectAvatars } from './avatar.selectors';
import { extractErrorMessage } from '@app/utils/error.utils';

@Injectable()
export class AvatarEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly avatarApiService = inject(AvatarApiService);
  private readonly preferencesFacade = inject(UserPreferencesFacade);
  private readonly logger = inject(LoggerService).scope('AvatarEffects');
  private readonly toastService = inject(ToastNotificationService);
  private readonly translate = inject(TranslateService);
  
  // Load avatars
  // Uses exhaustMap to ignore duplicate load requests while one is in progress
  // This prevents race conditions when multiple components request load simultaneously
  loadAvatars$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AvatarActions.loadAvatars),
      exhaustMap(({ page = 1, size = 100 }) => {
        this.logger.debug('Loading avatars', { page, size });
        return this.avatarApiService.listUserAvatars(page, size).pipe(
          map(response => {
            if ('items' in response) {
              return AvatarActions.loadAvatarsSuccess({ response });
            } else {
              throw new Error(response.detail || 'Failed to load avatars');
            }
          }),
          catchError(error => {
            this.logger.error('Load avatars failed:', error);
            return of(AvatarActions.loadAvatarsFailure({ error: error.message }));
          })
        );
      })
    )
  );

  // Create avatar
  createAvatar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AvatarActions.createAvatar),
      switchMap(({ image, faceName, gender }) =>
        this.avatarApiService.createAvatar(image, faceName, gender).pipe(
          map(response => {
            if ('id' in response) {
              this.toastService.showSuccess(this.translate.instant('toast.avatarCreated'));
              return AvatarActions.createAvatarSuccess({ avatar: response });
            } else {
              throw new Error(response.detail || 'Failed to create avatar');
            }
          }),
          catchError(error => {
            this.logger.error('Create avatar failed:', error);
            // Extract detailed error message using shared utility
            const errorMessage = extractErrorMessage(error, 'Failed to create avatar');
            // Show persistent error toast that requires user acknowledgment
            this.toastService.showPersistentError(errorMessage);
            return of(AvatarActions.createAvatarFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  // Get avatar status
  getAvatarStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AvatarActions.getAvatarStatus),
      switchMap(({ characterUid }) =>
        this.avatarApiService.getAvatarStatus(characterUid).pipe(
          map(response => {
            if ('id' in response) {
              return AvatarActions.getAvatarStatusSuccess({ avatar: response });
            } else {
              throw new Error(response.detail || 'Failed to get avatar status');
            }
          }),
          catchError(error => {
            this.logger.error('Get avatar status failed:', error);
            return of(AvatarActions.getAvatarStatusFailure({ error: error.message }));
          })
        )
      )
    )
  );

  // Update avatar
  updateAvatar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AvatarActions.updateAvatar),
      switchMap(({ id, updates }) =>
        this.avatarApiService.updateAvatar(id, updates).pipe(
          map(response => {
            if ('id' in response) {
              this.toastService.showSuccess(this.translate.instant('toast.avatarUpdated'));
              return AvatarActions.updateAvatarSuccess({ avatar: response });
            } else {
              throw new Error(response.detail || 'Failed to update avatar');
            }
          }),
          catchError(error => {
            this.logger.error('Update avatar failed:', error);
            this.toastService.showError(this.translate.instant('toast.avatarUpdateFailed'));
            return of(AvatarActions.updateAvatarFailure({ error: error.message }));
          })
        )
      )
    )
  );

  // Delete avatar
  deleteAvatar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AvatarActions.deleteAvatar),
      switchMap(({ id }) =>
        this.avatarApiService.deleteAvatar(id).pipe(
          map(() => {
            this.toastService.showSuccess(this.translate.instant('toast.avatarDeleted'));
            return AvatarActions.deleteAvatarSuccess({ id });
          }),
          catchError(error => {
            this.logger.error('Delete avatar failed:', error);
            this.toastService.showError(this.translate.instant('toast.avatarDeleteFailed'));
            return of(AvatarActions.deleteAvatarFailure({ error: error.message }));
          })
        )
      )
    )
  );

  // Delete all avatars
  deleteAllAvatars$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AvatarActions.deleteAllAvatars),
      switchMap(() =>
        this.avatarApiService.deleteAllAvatars().pipe(
          map((response) => {
            if (response.deleted_count === response.total_count) {
              // All deleted successfully
              this.toastService.showSuccess(this.translate.instant('toast.avatarsDeleted', { count: response.deleted_count }));
            } else if (response.deleted_count > 0) {
              // Partial success
              this.toastService.showInfo(
                this.translate.instant('toast.avatarsPartiallyDeleted', { deletedCount: response.deleted_count, totalCount: response.total_count })
              );
              if (response.errors && response.errors.length > 0) {
                this.logger.warn('Some avatar deletions failed:', response.errors);
              }
            } else {
              // All failed or no avatars
              this.toastService.showInfo(this.translate.instant('toast.noAvatarsToDelete'));
            }
            return AvatarActions.deleteAllAvatarsSuccess({ response });
          }),
          catchError(error => {
            this.logger.error('Delete all avatars failed:', error);
            this.toastService.showError(this.translate.instant('toast.avatarsDeleteFailed'));
            return of(AvatarActions.deleteAllAvatarsFailure({ error: error.message }));
          })
        )
      )
    )
  );

  // Set active avatar - updates user preferences with active_avatar_id
  // Navigation to /home happens after success
  setActiveAvatar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AvatarActions.setActiveAvatar),
      withLatestFrom(this.store.select(selectAvatars)),
      switchMap(([{ avatarId }, avatars]) => {
        const avatar = avatars.find(a => a.id === avatarId);
        if (!avatar) {
          return of(AvatarActions.setActiveAvatarFailure({ error: 'Avatar not found' }));
        }

        // Update user preferences with active_avatar_id
        // The optimistic update happens immediately in the user-preferences reducer
        this.preferencesFacade.updatePreferences({ active_avatar_id: avatarId });

        // Wait for user preferences update to complete
        return this.preferencesFacade.loading$.pipe(
          filter((loading: boolean) => loading), // Wait for loading to become true (update started)
          take(1),
          switchMap(() => this.preferencesFacade.loading$.pipe(
            filter((loading: boolean) => !loading), // Then wait for loading to become false (update complete)
            take(1),
            switchMap(() => this.preferencesFacade.error$.pipe(
              take(1),
              map(error => {
                if (!error) {
                  this.toastService.showSuccess(this.translate.instant('toast.avatarActivated', { avatarName: avatar.name }));
                  return AvatarActions.setActiveAvatarSuccess();
                } else {
                  this.logger.error('Set active avatar failed:', error);
                  this.toastService.showError(this.translate.instant('toast.avatarActivateFailed'));
                  return AvatarActions.setActiveAvatarFailure({ error: error || 'Unknown error' });
                }
              })
            ))
          ))
        );
      })
    )
  );

  // Navigate to home after avatar selection success
  navigateAfterAvatarSelection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AvatarActions.setActiveAvatarSuccess),
      tap(() => {
        this.router.navigate(['/home'], { state: { autoStart: true } });
      })
    ),
    { dispatch: false }
  );

  // Remove active avatar - clears active_avatar_id from user preferences
  removeActiveAvatar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AvatarActions.removeActiveAvatar),
      switchMap(() => {
        // Update user preferences with null active_avatar_id
        // The optimistic update happens immediately in the user-preferences reducer
        this.preferencesFacade.updatePreferences({ active_avatar_id: null });

        // Wait for user preferences update to complete
        return this.preferencesFacade.loading$.pipe(
          filter((loading: boolean) => loading), // Wait for loading to become true (update started)
          take(1),
          switchMap(() => this.preferencesFacade.loading$.pipe(
            filter((loading: boolean) => !loading), // Then wait for loading to become false (update complete)
            take(1),
            switchMap(() => this.preferencesFacade.error$.pipe(
              take(1),
              map(error => {
                if (!error) {
                  this.toastService.showSuccess(this.translate.instant('toast.avatarDeactivated'));
                  return AvatarActions.removeActiveAvatarSuccess();
                } else {
                  this.logger.error('Remove active avatar failed:', error);
                  this.toastService.showError(this.translate.instant('toast.avatarDeactivateFailed'));
                  return AvatarActions.removeActiveAvatarFailure({ error: error || 'Unknown error' });
                }
              })
            ))
          ))
        );
      })
    )
  );

  // Initialize avatars - just loads avatars
  // Active avatar is derived from preferences, no sync needed
  initializeAvatars$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AvatarActions.initializeAvatars),
      concatMap(() => [
        AvatarActions.loadAvatars({ page: 1, size: 100 })
      ])
    )
  );

  // Mark initialization as complete after avatars are loaded
  // No sync needed - activeAvatar is derived from preferences
  markInitializationComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AvatarActions.loadAvatarsSuccess),
      map(() => AvatarActions.initializeAvatarsSuccess())
    )
  );

  // Clear active_avatar_id from user preferences when all avatars are deleted
  clearActiveAvatarAfterDeleteAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AvatarActions.deleteAllAvatarsSuccess),
      switchMap(({ response }) => {
        // Only clear if at least one avatar was deleted
        if (response.deleted_count > 0) {
          this.logger.debug('Clearing active_avatar_id from user preferences after deleting all avatars');
          this.preferencesFacade.updatePreferences({ active_avatar_id: null });
        }
        return of(); // No further actions needed
      })
    ),
    { dispatch: false }
  );
}
